import * as React from 'react';

import type { InlineProps } from '@gitbook/slate-react';

/**
 * Check if an inline is active, meaning it has been hovered or the selection
 * is inside of it.
 * This hook is used for performance purpose, it allows to load a light component
 * and to only load the full one when active.
 */
export function useIsInlineActive(props: UseIsInlineActiveBySelectionProps): [
    boolean,
    {
        onMouseOver: () => void;
    },
] {
    const [isActive, setIsActive] = React.useState(false);
    const isActiveBySelection = useIsInlineActiveBySelection(props);
    const activate = React.useCallback(() => {
        setIsActive(true);
    }, []);

    React.useEffect(() => {
        if (isActiveBySelection) {
            activate();
        }
    }, [isActiveBySelection, activate]);

    return [isActive || isActiveBySelection, { onMouseOver: activate }];
}

type UseIsInlineActiveBySelectionProps = Pick<InlineProps, 'editor' | 'node'>;

/**
 * Check if the inline is considered active.
 * The selection must be inside the node.
 */
export function useIsInlineActiveBySelection(props: UseIsInlineActiveBySelectionProps) {
    const { node, editor } = props;
    return editor.value.selection.hasFocusIn(node) && editor.value.selection.hasAnchorIn(node);
}
